import { useState, useEffect } from "react";
import { config } from "../../constants";
import axiosInstance from "../../utils/axios";

/**
 * TikTok OAuth Component
 *
 * Provides TikTok authentication button for TapCreatorForm.
 * Uses server-side redirect flow (redirects to backend which redirects to TikTok).
 */
const TikTokOAuth = ({ onStateUpdate, tap }) => {
  const [isButtonDisabled, setButtonDisabled] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [advertisers, setAdvertisers] = useState([]);
  const [error, setError] = useState(null);

  // Listen for OAuth callback messages from popup
  useEffect(() => {
    const handleMessage = (event) => {
      // Security: Verify message origin comes from our taps backend
      const allowedOrigins = [
        config.url.TAPS_URL?.replace('/taps', ''),  // Production: https://api.datajolt.io
        'http://localhost:5000',                     // Local development
        'http://127.0.0.1:5000'
      ].filter(Boolean);

      if (!allowedOrigins.some(origin => event.origin.startsWith(origin))) {
        return; // Ignore messages from unknown origins
      }

      if (event.data?.type === 'tiktok_oauth_success') {
        setIsSuccessful(true);
        setButtonDisabled(true);
        setAdvertisers(event.data.advertisers || []);
        if (onStateUpdate) {
          onStateUpdate({
            tiktok_authenticated: true,
            tiktok_advertisers: event.data.advertisers,
            airbyte_tiktok_access_token: event.data.access_token,
            airbyte_tiktok_refresh_token: event.data.refresh_token,
          });
        }
      } else if (event.data?.type === 'tiktok_oauth_error') {
        setError(event.data.message || 'Authentication failed');
        setButtonDisabled(false);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onStateUpdate]);

  const handleAuthClick = async () => {
    setButtonDisabled(true);
    setError(null);

    try {
      // Fetch the TikTok auth URL with authentication
      const response = await axiosInstance.get(`${config.url.TAPS_URL}/api/oauth/tiktok/connect`);

      // Open centered popup window
      const width = 500, height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      window.open(
        response.data.auth_url,
        'TikTok Login',
        `width=${width},height=${height},left=${left},top=${top}`
      );
    } catch (err) {
      setError(err.message || "Failed to initiate TikTok authentication");
      setButtonDisabled(false);
    }
  };

  if (isSuccessful) {
    return (
      <div style={{ display: "flex", alignItems: "center", marginLeft: "10px" }}>
        <button className="tiktok-auth-button tiktok-auth-success" disabled>
          <div className="tiktok-button-content">
            <div className="tiktok-button-icon">
              <svg viewBox="0 0 24 24" style={{ width: "18px", height: "18px" }}>
                <path
                  fill="#28a745"
                  d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.88 2.89 2.89 0 0 1-2.88-2.88 2.89 2.89 0 0 1 2.88-2.88c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.16 15.7a6.34 6.34 0 0 0 6.33 6.34 6.34 6.34 0 0 0 6.34-6.34V9.26a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.01-.69z"
                />
              </svg>
            </div>
            <span className="tiktok-button-text">
              Authorized with TikTok
              {advertisers.length > 0 && ` (${advertisers.length} account${advertisers.length > 1 ? "s" : ""})`}
            </span>
          </div>
        </button>
        <style>{`
          .tiktok-auth-button {
            display: flex;
            align-items: center;
            padding: 10px 20px;
            border: 1px solid #ddd;
            border-radius: 4px;
            background: white;
            cursor: pointer;
            font-family: 'Roboto', sans-serif;
            font-size: 14px;
            font-weight: 500;
            transition: background-color 0.2s, box-shadow 0.2s;
          }
          .tiktok-auth-button:hover:not(:disabled) {
            background: #f8f8f8;
            box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          }
          .tiktok-auth-button:disabled {
            cursor: default;
            opacity: 0.8;
          }
          .tiktok-auth-success {
            border-color: #28a745;
            background: #f0fff4;
          }
          .tiktok-button-content {
            display: flex;
            align-items: center;
          }
          .tiktok-button-icon {
            margin-right: 10px;
            display: flex;
            align-items: center;
          }
          .tiktok-button-text {
            color: #333;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", marginLeft: "10px" }}>
      {error && (
        <div style={{ color: "#dc3545", marginBottom: "10px", fontSize: "14px" }}>
          Authentication failed: {error}
        </div>
      )}
      <button
        className="tiktok-auth-button"
        onClick={handleAuthClick}
        disabled={isButtonDisabled}
      >
        <div className="tiktok-button-content">
          <div className="tiktok-button-icon">
            <svg viewBox="0 0 24 24" style={{ width: "18px", height: "18px" }}>
              <path
                fill="#000"
                d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.88 2.89 2.89 0 0 1-2.88-2.88 2.89 2.89 0 0 1 2.88-2.88c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.16 15.7a6.34 6.34 0 0 0 6.33 6.34 6.34 6.34 0 0 0 6.34-6.34V9.26a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.01-.69z"
              />
            </svg>
          </div>
          <span className="tiktok-button-text">
            {isButtonDisabled ? "Waiting for TikTok..." : "Sign in with TikTok"}
          </span>
        </div>
      </button>
      <style>{`
        .tiktok-auth-button {
          display: flex;
          align-items: center;
          padding: 10px 20px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background: white;
          cursor: pointer;
          font-family: 'Roboto', sans-serif;
          font-size: 14px;
          font-weight: 500;
          transition: background-color 0.2s, box-shadow 0.2s;
          width: fit-content;
        }
        .tiktok-auth-button:hover:not(:disabled) {
          background: #f8f8f8;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .tiktok-auth-button:disabled {
          cursor: not-allowed;
          opacity: 0.7;
        }
        .tiktok-button-content {
          display: flex;
          align-items: center;
        }
        .tiktok-button-icon {
          margin-right: 10px;
          display: flex;
          align-items: center;
        }
        .tiktok-button-text {
          color: #333;
        }
      `}</style>
    </div>
  );
};

export default TikTokOAuth;
